import {Action, Card, closeCurrentDialog, openDialog} from 'platform/components';

import {isNil, isNotNil} from 'ramda';

import {FullVehicleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {composePath, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ServiceVehicleForm} from '../ServiceVehicleForm/ServiceVehicleForm';
import {ServiceVehicleFormMode} from '../ServiceVehicleForm/types/ServiceVehicleFormMode';
import {ServiceVehicleFormVariant} from '../ServiceVehicleForm/types/ServiceVehicleFormVariant';
import {AftersaleVehicleOverview} from './components/AftersaleVehicleOverview';
import {VehicleCreationRequest} from './components/MatchingVehicles';
import {VehicleMatchOrCreate} from './components/VehicleMatchOrCreate';

interface AftersalesVehicleWidgetCardProps extends RequiredTestIdProps {
  vehicle?: FullVehicleResponseBody | Nullish;
  onChange?: (vehicleId: string | null) => Promise<void> | void;
  onCreateVehicle?: (request: VehicleCreationRequest) => void;
  // The host renders the full form, since `libs/dms/shared` may not import it. Absent, the
  // shortened dialog below still runs.
  onEditVehicle?: (vehicleId: string) => void;
}

export function AftersalesVehicleWidgetCard(props: AftersalesVehicleWidgetCardProps) {
  const handleVehicleEdit = () => {
    const vehicleId = props.vehicle?.id;

    if (isNil(vehicleId)) {
      return;
    }

    if (isNotNil(props.onEditVehicle)) {
      props.onEditVehicle(vehicleId);
      return;
    }

    openDialog(
      <ServiceVehicleForm
        mode={ServiceVehicleFormMode.EDIT}
        variant={ServiceVehicleFormVariant.SIMPLE}
        vehicleId={vehicleId}
        onSuccess={closeCurrentDialog}
        onDiscard={closeCurrentDialog}
        data-testid={suffixTestId('edit', props)}
      />,
      {title: i18n.t('entity.vehicle.labels.editVehicle'), withAdditionalFooter: true}
    );
  };

  const handleVehicleChange = () => {
    openDialog(
      <VehicleMatchOrCreate
        onVehicleSelect={(vehicleId) => {
          closeCurrentDialog();
          props.onChange?.(vehicleId);
        }}
        onCreateVehicle={
          // Conditional, or MatchingVehicles' truthiness check never reaches its fallback.
          isNotNil(props.onCreateVehicle)
            ? (request) => {
                // The full form is a FullScreenModal; leaving this dialog open stacks two focus
                // traps. onCreated therefore omits the closeCurrentDialog onVehicleSelect does.
                closeCurrentDialog();
                props.onCreateVehicle?.({
                  prefill: request.prefill,
                  onCreated: (vehicleId) => props.onChange?.(vehicleId),
                });
              }
            : undefined
        }
        data-testid={suffixTestId('finder', props)}
      />,
      {title: i18n.t('entity.vehicle.labels.vehicle')}
    );
  };

  const actions: Action[] = [
    {
      title: i18n.t('general.labels.detail'),
      leftIcon: 'action/open_in_new',
      variant: 'link',
      type: 'button',
      onClick: () =>
        window.open(
          composePath(vehiclesRoutes.detail, {
            params: {id: props.vehicle?.id},
            isSearchParamsPreserved: false,
          })
        ),
    },
    {
      variant: 'link',
      type: 'button',
      title: i18n.t('general.actions.change'),
      leftIcon: 'action/autorenew',
      onClick: handleVehicleChange,
    },
    {
      variant: 'link',
      type: 'button',
      title: i18n.t('general.actions.edit'),
      leftIcon: 'image/edit',
      onClick: handleVehicleEdit,
    },
  ];

  return (
    <Card
      actions={isNotNil(props.vehicle) ? actions : undefined}
      title={i18n.t('entity.vehicle.labels.vehicle')}
      data-testid={suffixTestId('header', props)}
    >
      {props.vehicle ? (
        <AftersaleVehicleOverview
          vehicle={props.vehicle}
          data-testid={suffixTestId('overview', props)}
        />
      ) : (
        <VehicleMatchOrCreate
          onVehicleSelect={props.onChange}
          onCreateVehicle={props.onCreateVehicle}
          data-testid={suffixTestId('finder', props)}
        />
      )}
    </Card>
  );
}
